import { FormEvent, useEffect, useRef, useState } from "react";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { classNames, saveInputChangeInHookState } from "../../common/functions";
import { useNavigate } from "react-router-dom";
import useFirebaseAuthentication from "../../common/hooks/useFirebaseAuthentication";
import { sortBy } from "lodash";
import { SearchHistoryItem, useSearchHistory } from "../../buyerStateDefs";
import { usePostHog } from "posthog-js/react";
import { captureAnalyticsEvent } from "../../analytics";

export function SearchBar(props: SearchBarProps) {
  const [placeholder, setPlaceholder] = useState("");
  const [searchInput, setSearchInput] = useState(props.initialQuery || "");
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { authUser } = useFirebaseAuthentication();
  const [pastSearchSuggestions, setPastSearchSuggestions] = useState<
    SearchHistoryItem[]
  >([]);
  const totalUserSearchHistory = useSearchHistory(
    (state) => state.totalUserSearchHistory,
  );
  const inputRef = useRef(null);
  const [highlightedSuggestionIndex, setHighlightedSuggestionIndex] =
    useState(-1);

  // State to track if input is typed or pasted
  const [inputIsTyped, setInputIsTyped] = useState(false);
  const [inputIsPasted, setInputIsPasted] = useState(false);
  const posthog = usePostHog();

  useEffect(() => {
    // get local storage search history
    let pastSearches: SearchHistoryItem[] = JSON.parse(
      localStorage.getItem("searchHistory") || "[]",
    );

    if (totalUserSearchHistory && totalUserSearchHistory.length > 0) {
      // if logged in, use remote search history
      pastSearches = pastSearches.concat(
        totalUserSearchHistory.filter((item) => !!item.query),
      );
    }

    // remove duplicates
    const uniqueHistory: SearchHistoryItem[] = Object.values(
      Object.fromEntries(pastSearches.map((item) => [item.query, item])),
    );
    setPastSearchSuggestions(
      sortBy(uniqueHistory, (item) => -item.timestamp.seconds),
    );
  }, [authUser, totalUserSearchHistory, localStorage.getItem("searchHistory")]);

  const filteredSuggestions = (pastSearchSuggestions || [])
    .filter((suggestion) =>
      suggestion.query.toLowerCase().includes(searchInput.toLowerCase()),
    )
    .slice(0, 5);

  const navigate = useNavigate();

  const examples = [
    "Inconel 625 plate",
    "Inconel 718 hex bar 4ft long",
    '718 0.5" plate',
    "1.5m Round bar per AMS 5666",
  ];
  const animTimeOverflow = 20;

  const showClear = (isHovered || isFocused) && searchInput;
  const clearSearch = () => {
    setSearchInput("");
    if (inputRef.current) {
      (inputRef.current as HTMLInputElement).focus();
    }
  };

  const sendInputMethodTrackEvent = () => {
    let inputMethod = "unknown";
    if (inputIsTyped && !inputIsPasted) {
      inputMethod = "typed";
    } else if (inputIsPasted && !inputIsTyped) {
      inputMethod = "pasted";
    } else if (inputIsTyped && inputIsPasted) {
      inputMethod = "pasted_and_typed";
    }

    console.log("Input method:", inputMethod);

    captureAnalyticsEvent(posthog, "search_query_submitted", {
      searchQuery: searchInput,
      searchQueryInputMethod: inputMethod,
    });

    // Reset the state after sending the event
    setInputIsTyped(false);
    setInputIsPasted(false);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (searchInput.trim() === "") {
      return;
    }

    // Send input method tracking event
    sendInputMethodTrackEvent();

    // if not logged in, save search query to local storage
    if (!authUser) {
      const searchHistory = JSON.parse(
        localStorage.getItem("searchHistory") || "[]",
      );
      searchHistory.push({
        query: searchInput,
        timestamp: {
          seconds: Math.floor(Date.now() / 1000),
        },
      });
      localStorage.setItem("searchHistory", JSON.stringify(searchHistory));
    }

    navigate(`/?q=${searchInput}`);
  };

  useEffect(() => {
    setSearchInput(props.initialQuery || "");
  }, [props.initialQuery]);

  useEffect(() => {
    let placeholderIndex = 0;
    let placeholderLength = 0;
    const interval = setInterval(() => {
      if (
        placeholderLength ===
        examples[placeholderIndex].length + animTimeOverflow
      ) {
        placeholderIndex = (placeholderIndex + 1) % examples.length;
        placeholderLength = 0;
        return;
      }
      setPlaceholder(examples[placeholderIndex].slice(0, placeholderLength));
      placeholderLength++;
    }, 50);

    return () => clearInterval(interval);
  }, []);

  const handleSearchSuggestion = (suggestion: string) => {
    setIsFocused(false);
    setSearchInput(suggestion);
    navigate(`/?q=${suggestion}`);
  };

  const detechInputMethod = (e: React.FormEvent<HTMLInputElement>) => {
    const inputType = (e.nativeEvent as InputEvent).inputType;

    if (inputType === "insertText") {
      setInputIsTyped(true);
    }
    if (inputType === "insertFromPaste") {
      setInputIsPasted(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedSuggestionIndex((prev) =>
        prev < filteredSuggestions.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedSuggestionIndex((prev) =>
        prev > 0 ? prev - 1 : filteredSuggestions.length - 1,
      );
    } else if (e.key === "Enter" && highlightedSuggestionIndex >= 0) {
      e.preventDefault();
      handleSearchSuggestion(
        filteredSuggestions[highlightedSuggestionIndex].query,
      );
      setIsFocused(false);
      setHighlightedSuggestionIndex(-1);
    } else if (e.key === "Escape") {
      setIsFocused(false);
      setHighlightedSuggestionIndex(-1);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div
          className={classNames(
            "relative w-full",
            props.full ? "lg:w-[50%]" : null,
            props.full ? "mx-auto" : null,
          )}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <MagnifyingGlassIcon className="h-6 w-6 relative fa fa-search text-gray-400 top-[2.1em] left-3 h-14" />
          <input
            type="text"
            ref={inputRef}
            placeholder={props.full ? placeholder : "Search"}
            className="w-full rounded-full p-2 pl-11 focus:outline-none focus:ring-1 focus:ring-[#003636] focus:border-[#00062e]"
            onChange={saveInputChangeInHookState(setSearchInput)}
            onInput={detechInputMethod}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              // Add slight delay so click can register before hiding suggestions
              setTimeout(() => setIsFocused(false), 100);
            }}
            onKeyDown={handleKeyDown}
            value={searchInput}
          />
          {showClear && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-2 top-1/2 transform focus:outline-none"
            >
              <XMarkIcon className="h-6 w-6 fill-gray-400 hover:fill-gray-500" />
            </button>
          )}
          {isFocused && filteredSuggestions.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto text-left">
              <div className="pt-3 pb-1  px-2 text-[0.6em] font-medium text-gray-900">
                Past Searches
              </div>
              <ul>
                {filteredSuggestions.map((suggestion, index) => (
                  <li
                    key={index}
                    onMouseDown={() => handleSearchSuggestion(suggestion.query)}
                    className={classNames(
                      "hover:bg-gray-100 cursor-pointer py-2 px-2",
                      index === highlightedSuggestionIndex && "bg-gray-100",
                    )}
                  >
                    {suggestion.query}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {props.full && (
          <div className="mt-4">
            <button
              className="rounded-full bg-[#00062e] hover:bg-slate-900 text-white font-bold py-2 px-8"
              type="submit"
            >
              Find metal
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

type SearchBarProps = {
  full: boolean;
  onRfqUpload?: (rfqQueries: string[]) => void;
  initialQuery?: string;
};
